import React, { useContext, useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import useStyles from "./styles";
import { __t } from "@core/translation/translation";
import CustomTableWithPagination from "@components/CustomTableWithPagination";
import Typography from "@components/Typography";
import NumberFormat from "react-number-format";
import Tooltip from "@material-ui/core/Tooltip";
import clsx from "clsx";
import { IconButton, InputAdornment, OutlinedInput } from "@material-ui/core";
import transaction from "@api/transaction";
import { notification } from "@core/helper/notificationHelper";
import { Paper, Popover } from "@material-ui/core";
import CustomizedSnackbars from "@components/SnackBar";
import { getMarketPairDisplay } from "@core/utils/getCurrencyMeta";
import { MarketContext } from "@context/MarketContext";
import { useRangerOptional } from "@context/rangerContext";
import doneOrders from "@api/doneOrders";
import { GetCurrencyScale, GetMarketScale } from "@core/helper/getScale";
import { formatDisplayDateTime } from "@core/helper/formatDisplayDateTime";

const MarketYourTransaction = ({
  pageSize,
  pageNumber,
  className,
  emptySize = "default",
}) => {
  const classes = useStyles();
  const market = useContext(MarketContext);
  const ranger = useRangerOptional();
  const [state, setState] = useState({
    pageSize: pageSize,
    pageNumber: pageNumber,
    data: [],
    is_fetch: false,
    count: 0,
    open: false,
    anchorEl: null,
  });

  // WS4.4 — refresh user trades on private `trade` events
  useEffect(() => {
    if (!ranger?.onPrivateEvent) return undefined;
    return ranger.onPrivateEvent("trade", () => {
      market.setForceUpdate((v) => !v);
    });
  }, [ranger?.onPrivateEvent, market.setForceUpdate]);

  useEffect(() => {
    doneOrders
      .getList({
        params: {
          pageSize: state.pageSize,
          pageNumber: state.pageNumber,
        },
      })
      .then((data) => {
        setState((prevState) => ({
          ...prevState,
          data: data.content,
          is_fetch: true,
          count: data.count,
        }));
      })
      .catch((data) => {
        console.log(data?.message);
      });
  }, [market.forceUpdate]);

  const handlePopoverOpen = (event, popoverId) => {
    setState((prevState) => ({
      ...prevState,
      openedPopoverId: popoverId,
      anchorEl: event.target,
    }));
  };
  const handlePopoverClose = () => {
    setState((prevState) => ({
      ...prevState,
      openedPopoverId: null,
      anchorEl: null,
    }));
  };

  const handleDelete = (id) => {
    transaction
      .deleteTransaction({
        params: {
          id: id,
        },
      })
      .then((data) => {
        setState((prevState) => ({
          ...prevState,
          data: state.data.filter((item) => item.id !== id),
        }));
        notification("", "success", data.message);
      })
      .catch((data) => {
        notification(
          "",
          "danger",
          !!data && !!data?.message ? data?.message : "SERVER ERROR"
        );
      });
  };

  const TableOption = [
    {
      name: "currencyName",
      title: __t("Market"),
      render: (record, status) => {
        const { baseName, quoteName } = getMarketPairDisplay(record?.marketName);
        return (
          <div className={classes.coinLogoAndText}>
            <Typography variant="3xs" isBold>
              {baseName}
            </Typography>
            <Typography variant="3xs">{"/"}</Typography>
            <Typography variant="3xs">{quoteName}</Typography>
          </div>
        );
      },
    },
    {
      name: "created",
      title: __t("time"),
      date: true,
      render: (record, status) => (
        <Typography variant="3xs" className={classes.date}>
          {formatDisplayDateTime(record.created, { pattern: "full", input: "iso" })}
        </Typography>
      ),
    },
    {
      name: "type",
      title: __t("Order type"),
      render: (record, status) => (
        <div className={classes.orderTypeWrapper}>
          {record.side === "SELL" ? (
            <span className={clsx("icon-arrow-up", classes.orderTypeIconUp)} />
          ) : (
            <span
              className={clsx("icon-arrow-down", classes.orderTypeIconDown)}
            />
          )}

          <Typography variant="3xs">{__t(record.side)}</Typography>
        </div>
      ),
    },

    {
      name: "unitPrice",
      title: __t("price (toman)"),
      render: (record, status) => (
        <Typography variant="3xs">
          <NumberFormat
            value={record.unitPrice}
            displayType={"text"}
            thousandSeparator={true}
          />
        </Typography>
      ),
    },
    {
      name: "size",
      title: __t("Total Size"),
      render: (record, status) => (
        <Typography variant="3xs">
          <NumberFormat
            value={record.size}
            displayType={"text"}
            thousandSeparator={true}
          />
        </Typography>
      ),
    },
    {
      name: "Total",
      title: __t("Toatal Amount(Toman)"),
      render: (record, status) => (
        <Typography variant="3xs">
          <NumberFormat
            value={Math.ceil(record.unitPrice * record.size)}
            displayType={"text"}
            thousandSeparator={true}
          />
        </Typography>
      ),
    },
    {
      name: "fee",
      title: __t("Fee"),
      render: (record, status) => {
        const { baseUnit, quoteUnit } = getMarketPairDisplay(record?.marketName);
        return (
          <Typography variant="3xs" className={classes.orderTypeWrapper}>
            <NumberFormat
              value={
                record.side === "SELL"
                  ? record.fee.toFixed(GetMarketScale(quoteUnit))
                  : record.fee.toFixed(GetCurrencyScale(baseUnit))
              }
              displayType={"text"}
              thousandSeparator={true}
            />
            <Typography
              variant="3xs"
              color="secondary.tint40"
              className={classes.seperator}
            >
              ({record.side === "SELL" ? baseUnit : quoteUnit})
            </Typography>
          </Typography>
        );
      },
    },
    {
      lastFixed: true,
      render: (record, status) => (
        <div className={classes.wrapperLastCol}>
          {record.status === "OPEN" && (
            <Tooltip arrow title={__t("delete")} placement="top-center">
              <IconButton
                size="small"
                onClick={() => handleDelete(record.id)}
                className={classes.xBtn}
                color="primary"
              >
                <span className="icon-x" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip arrow title={__t("See details")} placement="top-center">
            <IconButton
              size="small"
              onClick={(e) => handlePopoverOpen(e, record.id)}
              className={classes.detailBtn}
              color="primary"
            >
              <span
                className={
                  state.openedPopoverId === record.id ? "icon-x" : "icon-eye"
                }
              />
            </IconButton>
          </Tooltip>
          {record.id === state.openedPopoverId && (
            <Popover
              open={state.openedPopoverId === record.id}
              anchorEl={state.anchorEl}
              onClose={handlePopoverClose}
              className={classes.popOverContainer}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <Paper className={classes.popOver} elevation={0}>
                <Grid container direction="column" spacing={10}>
                  <Grid
                    item
                    container
                    direction="row"
                    justify="space-between"
                    alignItems="center"
                  >
                    <Grid item>
                      <Typography variant="2xs">{__t("Order ID")}</Typography>
                    </Grid>
                    <Grid item>
                      <OutlinedInput
                        name="code"
                        fullWidth
                        value="btc-irt6486"
                        disabled
                        className={classes.input}
                        endAdornment={
                          <InputAdornment variant="filled" position="end">
                            <CustomizedSnackbars
                              text="btc-irt6486"
                              message={__t("Copied successfully")}
                            />
                          </InputAdornment>
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Typography variant="2xs" classes={classes.popOverText}>
                      * واریز مبلغ درخواستی بین ساعت‌های ۱۴ تا ۲۰ شنبه (امروز)
                      به تاربخ ۲شهریور ۱۳۹۹ انجام میشود
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Popover>
          )}
        </div>
      ),
    },
  ];
  return (
    <>
      <CustomTableWithPagination
        TableOption={TableOption}
        data={state.data}
        is_fetch={state.is_fetch}
        pagination={false}
        className={className}
        type="sticky"
        emptySize={emptySize}
      />
    </>
  );
};

export default MarketYourTransaction;
